import React, { useState, useEffect } from 'react';

// Function → carton source mapping
const FN_CARTON_MAP = {
  // Receive Dock functions
  'Vendor Line Load': 'pid', 'PID Manual Divert': 'pid', 'ReceiveParcel Unload': 'pid',
  'Receive Dock Crew': 'pid', 'PID Box Opener': 'pid',
  'ReceiveLine Unloader': 'aros', 'PID Sort Line': 'aros',
  'Vendor Sort': 'pr', 'IB Robotics Operator': 'pr', 'PID Truck Unload': 'pr',
  'Inbound Dock Clerk': 'ibCartons', 'Receive Dock TDR': 'ibCartons',
  'Pallet Unload': 'ibCartons', 'FreightID': 'ibCartons',
  // Receive Support functions
  'Offline_Mods': 'pr', 'Rcv WaterSpider': 'pr', 'Case Rcv WaterSpider': 'pr',
  'MHE Support': 'pid', 'General Audits': 'zero',
  // Transfer Out functions
  'Wall Builder': 'fluidDirect', 'Robotics Operator': 'fluidOffline',
  'Transfer Out Packing': 'fluidOffline', 'Fluid Load - Case': 'fluidDirect',
  'Jackpot Kickout': 'aros', 'Robotic Problem Solv': 'aros',
  'Robotic Water Spider': 'aros', 'Transfer Out Kaizen': 'eol',
  'Water Spider': 'eol',
  'TO Lead/PA': 'obCartons', 'TO Problem Solve': 'obCartons',
  'Transfer Out Ambssdr': 'obCartons', 'TransferOut Training': 'obCartons',
  'Trans Out Overflow': 'obCartons',
  // Transfer Out Dock functions
  'Dock Pallet Loader': 'pr', 'TransferOut DockCrew': 'pr', 'IXD Pallet Loader': 'pr',
  'Tote Replenishment': 'eol', 'Ship Clerk': 'obCartons', 'Outbound Dock Crew': 'obCartons',
};

// Which functions roll up to which line item
const LINE_ITEM_FUNCTIONS = {
  receiveDock: ['Vendor Line Load', 'PID Manual Divert', 'ReceiveParcel Unload', 'Receive Dock Crew', 'PID Box Opener', 'ReceiveLine Unloader', 'PID Sort Line', 'Vendor Sort', 'IB Robotics Operator', 'PID Truck Unload', 'Inbound Dock Clerk', 'Receive Dock TDR', 'Pallet Unload', 'FreightID'],
  receiveSupport: ['Offline_Mods', 'Rcv WaterSpider', 'Case Rcv WaterSpider', 'MHE Support', 'General Audits'],
  transferOut: ['Wall Builder', 'Robotics Operator', 'Transfer Out Packing', 'Fluid Load - Case', 'Jackpot Kickout', 'Robotic Problem Solv', 'Robotic Water Spider', 'Transfer Out Kaizen', 'Water Spider', 'TO Lead/PA', 'TO Problem Solve', 'Transfer Out Ambssdr', 'TransferOut Training', 'Trans Out Overflow'],
  transferOutDock: ['Dock Pallet Loader', 'TransferOut DockCrew', 'IXD Pallet Loader', 'Tote Replenishment', 'Ship Clerk', 'Outbound Dock Crew'],
};

// Line item carton source (for calculating derived rate)
const LINE_CARTON_MAP = {
  receiveDock: 'ibCartons', receiveSupport: 'ibCartons',
  transferOut: 'obCartons', transferOutDock: 'obCartons',
  daBldgTotal: 'obCartons', totalInbound: 'ibCartons',
  transferOutSummary: 'obCartons', support: 'totalCartons',
  adminHrIt: 'totalCartons', throughput: 'totalCartons',
  _prodThroughput: 'totalCartons',
};

export default function VolumeForecast({ api, site, forecastByLine, filteredData, weekInputs, setWeekInputs }) {
  const [saved, setSaved] = useState(false);

  const saveInputs = async () => {
    await fetch(`${api}/api/fclm/cache`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ site, date: 'VOLFORECAST', shift_type: 'inputs', data: { weeks: weekInputs } }),
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const updateWeek = (weekIdx, field, value) => {
    const updated = [...weekInputs];
    updated[weekIdx] = { ...updated[weekIdx], [field]: value };
    setWeekInputs(updated);
  };

  const fillAll = () => setWeekInputs(Array(13).fill(null).map(() => ({ ...weekInputs[0] })));

  // Calculate volumes for a week's inputs
  const getVolumes = (inp) => {
    const prPct = 100 - inp.pidPct - inp.arosPct;
    const pid = Math.round(inp.totalCartons * inp.pidPct / 100);
    const aros = Math.round(inp.totalCartons * inp.arosPct / 100);
    const pr = Math.round(inp.totalCartons * prPct / 100);
    const eol = Math.round(pid * inp.eolPct / 100);
    const fluidDirect = pid - eol;
    const fluidOffline = eol + aros;
    const each = Math.round(inp.totalCartons * (inp.eachPct || 0.01) / 100);
    const caseVol = Math.round(inp.totalCartons * (inp.casePct || 0.01) / 100);
    const prep = Math.round(inp.totalCartons * (inp.prepPct || 0.01) / 100);
    return { ibCartons: inp.totalCartons, obCartons: inp.totalCartons, totalCartons: inp.totalCartons * 2, pid, aros, pr, eol, fluidDirect, fluidOffline, each, caseVol, prep, zero: 0 };
  };

  // Get function F1+ rate from forecast (using funcForecasts from parent would be ideal, but recalc here)
  const getFuncRate = (fnName, weekIdx) => {
    // Use last 4 complete weeks, top 3 rates, + ramp
    const last4 = filteredData.length > 1 ? filteredData.slice(-5, -1) : [];
    const rates = last4.map(w => {
      const allFuncs = Object.values(w.functions || {}).flat();
      const fn = allFuncs.find(f => f.name === fnName);
      return fn?.rate || 0;
    }).filter(r => r > 0);
    rates.sort((a, b) => b - a);
    const top3 = rates.slice(0, 3);
    const baseline = top3.length > 0 ? top3.reduce((s, v) => s + v, 0) / top3.length : 0;
    const rampPct = weekInputs[0]?.rampPct || 0.5;
    return baseline * (1 + (rampPct / 100) * (weekIdx + 1));
  };

  // Calculate function hours for a week
  const getFuncHours = (fnName, weekIdx) => {
    const vols = getVolumes(weekInputs[weekIdx]);
    const cartonKey = FN_CARTON_MAP[fnName];
    if (!cartonKey || cartonKey === 'zero') return 0;
    const vol = vols[cartonKey] || 0;
    const rate = getFuncRate(fnName, weekIdx);
    return rate > 0 ? vol / rate : 0;
  };

  // Calculate line item hours (sum of functions) for a week
  const getLineHours = (lineId, weekIdx) => {
    const functions = LINE_ITEM_FUNCTIONS[lineId];
    if (!functions) {
      // For lines without function breakdown, use volume / forecast rate
      const vols = getVolumes(weekInputs[weekIdx]);
      const volKey = LINE_CARTON_MAP[lineId];
      const vol = vols[volKey] || 0;
      const rate = forecastByLine[lineId]?.forecast?.[weekIdx] || 0;
      return rate > 0 ? vol / rate : 0;
    }
    return functions.reduce((sum, fn) => sum + getFuncHours(fn, weekIdx), 0);
  };

  // Calculate derived line item rate (cartons / summed function hours)
  const getLineRate = (lineId, weekIdx) => {
    const vols = getVolumes(weekInputs[weekIdx]);
    const volKey = LINE_CARTON_MAP[lineId];
    const vol = vols[volKey] || 0;
    const hours = getLineHours(lineId, weekIdx);
    return hours > 0 ? vol / hours : 0;
  };

  const cellStyle = { padding: '3px 6px', textAlign: 'center', fontSize: '0.7rem', border: '1px solid #ccc' };
  const headerStyle = { ...cellStyle, fontWeight: 700, background: '#1565c0', color: '#fff' };

  const DISPLAY_LINES = [
    { id: 'receiveDock', name: 'Receive Dock' },
    { id: 'receiveSupport', name: 'Receive Support' },
    { id: 'transferOut', name: 'Transfer Out' },
    { id: 'transferOutDock', name: 'Transfer Out Dock' },
    { id: 'daBldgTotal', name: 'DA Bldg Total' },
    { id: 'adminHrIt', name: 'Admin/HR/IT' },
    { id: 'totalInbound', name: 'Total Inbound' },
    { id: 'transferOutSummary', name: 'Transfer-Out' },
    { id: 'support', name: 'Support' },
    { id: 'throughput', name: 'THROUGHPUT' },
  ];

  return (
    <div className="card" style={{ overflowX: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 className="section-title" style={{ margin: 0 }}>Volume Forecast — Bottom-Up Hours Plan</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={fillAll} style={{ fontSize: '0.75rem', padding: '4px 10px' }}>Copy F1 → All</button>
          <button className="btn btn-secondary" onClick={() => {
            const headers = ['Input', ...Array(13).fill(0).map((_, i) => `F${i+1}`)].join(',');
            const rows = [['Total Cartons', ...weekInputs.map(w => w.totalCartons)].join(','), ['PID %', ...weekInputs.map(w => w.pidPct)].join(','), ['AROS %', ...weekInputs.map(w => w.arosPct)].join(','), ['EOL %', ...weekInputs.map(w => w.eolPct)].join(',')];
            const blob = new Blob([[headers, ...rows].join('\n')], { type: 'text/csv' });
            const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'volume_forecast_template.csv'; a.click();
          }} style={{ fontSize: '0.75rem', padding: '4px 10px' }}>📥 Template</button>
          <label className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '4px 10px', cursor: 'pointer' }}>
            📤 Upload
            <input type="file" accept=".csv" style={{ display: 'none' }} onChange={e => {
              const file = e.target.files[0]; if (!file) return;
              file.text().then(text => { const lines = text.trim().split('\n'); const newInputs = [...weekInputs]; lines.slice(1).forEach(line => { const cols = line.split(','); const label = cols[0].trim().toLowerCase(); const vals = cols.slice(1).map(v => parseFloat(v) || 0); vals.forEach((v, i) => { if (i >= 13) return; if (label.includes('total')) newInputs[i] = { ...newInputs[i], totalCartons: v }; else if (label.includes('pid')) newInputs[i] = { ...newInputs[i], pidPct: v }; else if (label.includes('aros')) newInputs[i] = { ...newInputs[i], arosPct: v }; else if (label.includes('eol')) newInputs[i] = { ...newInputs[i], eolPct: v }; }); }); setWeekInputs(newInputs); });
            }} />
          </label>
          <button className="btn btn-primary" onClick={saveInputs} style={{ fontSize: '0.75rem', padding: '4px 10px' }}>{saved ? '✅ Saved!' : 'Save'}</button>
        </div>
      </div>

      {/* Volume Inputs */}
      <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: 16 }}>
        <thead><tr><th style={{ ...headerStyle, textAlign: 'left', minWidth: 120 }}>Input</th>{weekInputs.map((_, i) => <th key={i} style={headerStyle}>F{i+1}</th>)}</tr></thead>
        <tbody>
          {[{ key: 'totalCartons', label: 'Total Cartons' }, { key: 'pidPct', label: 'PID/Sorter %' }, { key: 'arosPct', label: 'AROS %' }, { key: '_prPct', label: 'PR % (auto)' }, { key: 'eolPct', label: 'EOL % (of Sorter)' }, { key: 'cpp', label: 'CPP (Cartons/Pallet)' }, { key: 'eachPct', label: 'Each % (of IB)' }, { key: 'casePct', label: 'Case % (of IB)' }, { key: 'prepPct', label: 'Prep % (of IB)' }].map(row => (
            <tr key={row.key}><td style={{ ...cellStyle, textAlign: 'left', fontWeight: 600, background: '#f9fafb' }}>{row.label}</td>
              {weekInputs.map((w, i) => <td key={i} style={{ ...cellStyle, padding: '2px' }}>{row.key === '_prPct' ? <span style={{ fontSize: '0.7rem', color: '#666' }}>{(100 - w.pidPct - w.arosPct).toFixed(0)}%</span> : <input type="text" value={w[row.key] || (row.key === 'cpp' ? '28.2' : '')} onChange={e => updateWeek(i, row.key, parseFloat(e.target.value) || 0)} style={{ width: '100%', border: 'none', textAlign: 'center', fontSize: '0.7rem', padding: '2px' }} />}</td>)}
            </tr>
          ))}
        </tbody>
      </table>

    </div>
  );
}
